"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProductDetails() {
  const label = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const divider = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const leftPanel = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const rightPanel = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const specs = [{
    tag: "AEROSPACE GRADE — TITANIUM ALLOY",
    heading: "Strength, redefined.",
    body: "Forged from high-density titanium, our chassis offers an unparalleled strength-to-weight ratio. Precision-machined to aerospace tolerances, the frame provides a rigid, corrosion-resistant foundation that ensures structural longevity while maintaining a feather-light profile for daily performance.",
    specs: [{
      label: "MATERIAL",
      value: "GRADE 5"
    }, {
      label: "FINISH",
      value: "BRUSHED"
    }, {
      label: "WEIGHT",
      value: "42G"
    }],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/titanium_chassis_macro.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/titanium_chassis_macro.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/titanium_chassis_macro.webp-wvc-sizes",
      alt: "Macro photograph of brushed titanium alloy surface"
    }
  }, {
    tag: "CARBON FIBER — REINFORCED CORE",
    heading: "Resilience, engineered.",
    body: "Utilizing multi-axial carbon fiber weaves, our internal components are shielded by a high-impact composite shell. This advanced material absorbs kinetic energy and dissipates thermal stress, ensuring that the internal mechanics remain protected under the most demanding environmental conditions.",
    specs: [{
      label: "STRUCTURE",
      value: "3K WEAVE"
    }, {
      label: "DENSITY",
      value: "1.6G/CM3"
    }, {
      label: "SHIELDING",
      value: "THERMAL"
    }],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/carbon_fiber_weave.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/carbon_fiber_weave.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/carbon_fiber_weave.png-wvc-sizes",
      alt: "Macro photograph of carbon fiber weave pattern"
    }
  }];
  return <section data-section-id="4004" id="product-details" className="bg-background text-foreground py-32 md:py-[200px] px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={label.ref} className={`text-center font-mono text-xs tracking-[0.3em] text-primary mb-8 transition-all duration-700 ease-out ${label.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          02 / ENGINEERING
        </div>

        {}
        <h2 ref={heading.ref} className={`font-default font-bold text-center text-4xl md:text-5xl lg:text-[56px] leading-[1.1] text-foreground mb-20 md:mb-28 transition-all duration-700 ease-out delay-100 ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Built for the extreme.
        </h2>

        {}
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-0">
          {}
          <div ref={divider.ref} aria-hidden="true" className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-px bg-border origin-center" style={{
          height: divider.isVisible ? "100%" : "0%",
          transition: "height 900ms ease-out"
        }} />

          {}
          <div ref={leftPanel.ref} data-index={0} className={`md:pr-12 lg:pr-16 transition-all duration-[900ms] ease-out ${leftPanel.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="aspect-[4/5] overflow-hidden bg-muted mb-10 rounded-lg shadow-md">
              <img src={specs[0].image.src} data-wvc-srcset={specs[0].image.srcset} data-wvc-sizes={specs[0].image.sizes} alt={specs[0].image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>

            <div className="font-mono text-[11px] tracking-[0.25em] text-primary mb-6">
              {specs[0].tag}
            </div>

            <h3 className="font-default font-bold text-3xl md:text-[36px] leading-tight text-foreground mb-6">
              {specs[0].heading}
            </h3>

            <p className="font-default text-base leading-relaxed text-foreground/80 mb-10 max-w-md">
              {specs[0].body}
            </p>

            <div className="border-t border-border pt-6 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[10px] tracking-[0.2em] text-foreground">
              {specs[0].specs.map((s, i) => <div data-index={i} key={i} className="flex items-center gap-2">
                  <span className="text-foreground/60">{s.label}</span>
                  <span className="text-foreground">·</span>
                  <span className="text-foreground">{s.value}</span>
                </div>)}
            </div>
          </div>

          {}
          <div ref={rightPanel.ref} data-index={1} className={`md:pl-12 lg:pl-16 transition-all duration-[900ms] ease-out ${rightPanel.isVisible ? "opacity-100 translate-y-0 delay-200" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: rightPanel.isVisible ? "200ms" : "0ms"
        }}>
            <div className="aspect-[4/5] overflow-hidden bg-muted mb-10 rounded-lg shadow-md">
              <img src={specs[1].image.src} data-wvc-srcset={specs[1].image.srcset} data-wvc-sizes={specs[1].image.sizes} alt={specs[1].image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>

            <div className="font-mono text-[11px] tracking-[0.25em] text-primary mb-6">
              {specs[1].tag}
            </div>

            <h3 className="font-default font-bold text-3xl md:text-[36px] leading-tight text-foreground mb-6">
              {specs[1].heading}
            </h3>

            <p className="font-default text-base leading-relaxed text-foreground/80 mb-10 max-w-md">
              {specs[1].body}
            </p>

            <div className="border-t border-border pt-6 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[10px] tracking-[0.2em] text-foreground">
              {specs[1].specs.map((s, i) => <div data-index={i} key={i} className="flex items-center gap-2">
                  <span className="text-foreground/60">{s.label}</span>
                  <span className="text-foreground">·</span>
                  <span className="text-foreground">{s.value}</span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}